import {
  Building2,
  Check,
  Download,
  FileArchive,
  FileSpreadsheet,
  LayoutGrid,
  Plus,
  Search,
  Wallet,
} from 'lucide-react';
import { Simbolo } from '../../componentes/Marca';
import { Etiqueta, Progresso, cx } from '../../componentes/ui';
import css from './Mockups.module.css';

/*
 * Miniaturas do produto, desenhadas em HTML/CSS.
 *
 * Mostram a cara real das telas (mesmos componentes do painel: Etiqueta,
 * Progresso) em vez de uma imagem que envelhece a cada ajuste de layout. Os
 * números são ilustrativos — por isso tudo aqui é `aria-hidden`: leitor de tela
 * e buscador não devem tomar exemplo por dado.
 */

const BUSCAS = [
  {
    nome: 'EFD ICMS/IPI · jan–dez 2025',
    origem: 'SPED · 12 arquivos',
    chaves: '12.480',
    valor: 1,
    status: 'Concluída',
    tom: 'ok' as const,
    detalhe: '12.311 XMLs',
  },
  {
    nome: 'Compras de outubro',
    origem: 'Chaves coladas',
    chaves: '428',
    valor: 0.64,
    status: 'Em andamento',
    tom: 'info' as const,
    detalhe: '274 de 428',
  },
  {
    nome: 'Fornecedores 2024',
    origem: 'CSV',
    chaves: '3.902',
    valor: 1,
    status: 'Concluída',
    tom: 'ok' as const,
    detalhe: '3.902 XMLs',
  },
  {
    nome: 'EFD Contribuições · 2024',
    origem: 'SPED · .zip',
    chaves: '48.016',
    valor: 0.18,
    status: 'Em andamento',
    tom: 'info' as const,
    detalhe: '8.643 de 48.016',
  },
];

/** A tela de Downloads do painel, em miniatura — o visual da chamada. */
export function JanelaDownloads() {
  return (
    <div className={css.palco} aria-hidden="true">
      <div className={css.janela}>
        <div className={css.lateral}>
          <span className={css.lateralMarca}>
            <Simbolo tamanho={20} cor="var(--texto-inverso)" />
          </span>
          <span className={css.lateralItem}>
            <LayoutGrid />
          </span>
          <span className={css.lateralItem}>
            <Search />
          </span>
          <span className={cx(css.lateralItem, css.lateralAtivo)}>
            <Download />
          </span>
          <span className={css.lateralItem}>
            <FileSpreadsheet />
          </span>
          <span className={css.lateralItem}>
            <Wallet />
          </span>
        </div>

        <div className={css.corpo}>
          <div className={css.topoJanela}>
            <div>
              <span className={css.tituloJanela}>Downloads</span>
              <span className={css.subJanela}>4 buscas · últimos 2 dias</span>
            </div>
            <span className={css.botaoFalso}>
              <Plus />
              Nova busca
            </span>
          </div>

          <div className={css.indicadores}>
            <div className={css.indicador}>
              <span>XMLs entregues</span>
              <b>24.856</b>
            </div>
            <div className={css.indicador}>
              <span>Em andamento</span>
              <b>2</b>
            </div>
            <div className={css.indicador}>
              <span>Não cobradas</span>
              <b className={css.verde}>169</b>
            </div>
          </div>

          <div className={css.tabela}>
            <div className={cx(css.linha, css.linhaCabecalho)}>
              <span>Busca</span>
              <span className={css.colChaves}>Chaves</span>
              <span>Progresso</span>
              <span className={css.colStatus}>Status</span>
            </div>
            {BUSCAS.map((b) => (
              <div className={css.linha} key={b.nome}>
                <span className={css.celulaNome}>
                  <b>{b.nome}</b>
                  <small>{b.origem}</small>
                </span>
                <span className={cx(css.colChaves, css.num)}>{b.chaves}</span>
                <span className={css.celulaProgresso}>
                  <Progresso valor={b.valor} />
                  <small>{b.detalhe}</small>
                </span>
                <span className={css.colStatus}>
                  <Etiqueta tom={b.tom} pulsando={b.tom === 'info'}>
                    {b.status}
                  </Etiqueta>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={css.flutuante}>
        <span className={css.flutuanteIcone}>
          <FileArchive />
        </span>
        <span className={css.flutuanteTexto}>
          <b>ZIP pronto</b>
          <small>12.311 XMLs originais</small>
        </span>
        <span className={css.flutuanteAcao}>
          <Download />
        </span>
      </div>
    </div>
  );
}

/** O resumo que aparece depois de enviar o SPED. */
export function ResumoSped() {
  const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return (
    <div className={css.cartao} aria-hidden="true">
      <div className={css.cartaoTopo}>
        <span className={css.arquivo}>
          <FileSpreadsheet />
        </span>
        <span className={css.celulaNome}>
          <b>EFD ICMS/IPI 2025.zip</b>
          <small>12 arquivos · 1 ECD reconhecido e ignorado</small>
        </span>
        <Etiqueta tom="ok">Analisado</Etiqueta>
      </div>

      <div className={css.numerosSped}>
        <div>
          <b>12.480</b>
          <span>notas no período</span>
        </div>
        <div>
          <b>9.214</b>
          <span>entradas</span>
        </div>
        <div>
          <b>3.266</b>
          <span>saídas</span>
        </div>
      </div>

      <div className={css.filtro}>
        <span className={css.rotuloFiltro}>Competência</span>
        <div className={css.meses}>
          {meses.map((m, i) => (
            <span key={m} className={cx(css.mes, i < 6 && css.mesAtivo)}>
              {m}
            </span>
          ))}
        </div>
      </div>

      <div className={css.filtro}>
        <span className={css.rotuloFiltro}>CFOP</span>
        <div className={css.chips}>
          <span className={cx(css.chip, css.chipAtivo)}>1102 · 4.120</span>
          <span className={cx(css.chip, css.chipAtivo)}>2102 · 1.388</span>
          <span className={css.chip}>1556 · 402</span>
          <span className={css.chip}>5102 · 2.977</span>
        </div>
      </div>

      <div className={css.rodapeCartao}>
        <span>
          <b>5.508 notas</b> selecionadas
        </span>
        <span className={css.botaoFalso}>
          <Download />
          Baixar XMLs
        </span>
      </div>
    </div>
  );
}

/** Chaves coladas, já validadas — o caminho da lista pronta. */
export function ChavesColadas() {
  const chaves = [
    '3525 1007 1234 5678 0001 5500 1000 0457 1234 5678 9012',
    '3525 1033 9988 7766 0001 5500 1000 0123 9874 4123 4567',
    '4125 1045 5112 2334 4000 1550 0200 0078 1655 9012 3456',
    '3125 1090 0112 2334 0001 5500 1000 0334 7102 8765 4321',
  ];
  return (
    <div className={css.cartao} aria-hidden="true">
      <div className={css.cartaoTopo}>
        <span className={css.celulaNome}>
          <b>Chaves de acesso</b>
          <small>Cole, digite ou envie um CSV</small>
        </span>
      </div>
      <div className={css.areaChaves}>
        {chaves.map((c) => (
          <code key={c}>{c}</code>
        ))}
        <span className={css.mais}>+ 424 chaves</span>
      </div>
      <div className={css.validacao}>
        <span>
          <Check />
          <b>428 chaves válidas</b>
        </span>
        <span>3 repetidas descartadas</span>
      </div>
      <div className={css.rodapeCartao}>
        <span>
          Custo calculado <b>antes</b> de começar
        </span>
        <span className={css.botaoFalso}>
          <Search />
          Buscar 428 XMLs
        </span>
      </div>
    </div>
  );
}

const NOTAS = [
  { emitente: 'Distribuidora Alfa Ltda', detalhe: 'NF 45.812 · R$ 18.430,00', status: 'XML completo', tom: 'ok' as const },
  { emitente: 'Metalúrgica Serra Azul', detalhe: 'NF 9.104 · R$ 4.276,90', status: 'XML completo', tom: 'ok' as const },
  { emitente: 'Transportes Rota Sul', detalhe: 'NF 120.337 · R$ 912,40', status: 'Ciência enviada', tom: 'info' as const },
  { emitente: 'Papelaria Central ME', detalhe: 'NF 3.551 · R$ 238,15', status: 'XML completo', tom: 'ok' as const },
];

/** Notas de entrada que chegaram sozinhas pela captura via certificado A1. */
export function NotasCapturadas() {
  return (
    <div className={css.cartao} aria-hidden="true">
      <div className={css.cartaoTopo}>
        <span className={css.arquivo}>
          <Building2 />
        </span>
        <span className={css.celulaNome}>
          <b>Comercial Exemplo Ltda</b>
          <small>CNPJ 12.345.678/0001-90 · consultada há 14 min</small>
        </span>
        <Etiqueta tom="ok">Ativa</Etiqueta>
      </div>

      <div className={css.numerosSped}>
        <div>
          <b>186</b>
          <span>notas no mês</span>
        </div>
        <div>
          <b>184</b>
          <span>XMLs completos</span>
        </div>
        <div>
          <b>1</b>
          <span>cancelada</span>
        </div>
      </div>

      <div className={css.notas}>
        {NOTAS.map((n) => (
          <div key={n.emitente} className={css.nota}>
            <span className={css.celulaNome}>
              <b>{n.emitente}</b>
              <small>{n.detalhe}</small>
            </span>
            <Etiqueta tom={n.tom}>{n.status}</Etiqueta>
          </div>
        ))}
      </div>

      <div className={css.rodapeCartao}>
        <span>
          Sem pedir <b>nada</b> ao cliente
        </span>
        <span className={css.botaoFalso}>
          <Download />
          Baixar ZIP do mês
        </span>
      </div>
    </div>
  );
}
